import { createEffect, createSignal, For, onCleanup, onMount } from "solid-js"
import { createStore } from "solid-js/store"
import { fetchGet } from "../library/common";
import { ArtworkImage, ListItem } from "../library/types";
import { A, useRouteData } from "@solidjs/router";
import { CMS } from "../components/universal/cms";
import { ModelProvider, ModelSelect, ModelTextBox } from "../components/model-form-elements";
import { useForm } from "../components/universal/validation";
import { PageList } from "../components/page-list";
import { Img } from "../components/universal/image";

export type Filter = {
    Page: number;
    PageRows: number;
    Gender: string;
    ArtistName: string;
    MovementId: string;
    NationalityId: string;
    ProfessionId: string;
    Order: string;
    Direction: string;
}

export type Search = {
    Count: number;
    MaxPage: number;
    Page: number;
    Results: ArtworkImage[];
}

export type Resources = {
    Genders: ListItem[];
    Movements: ListItem[];
    Professions: ListItem[];
    Nationalities: ListItem[];
    SortOptions: ListItem[];
    Directions: ListItem[];
}

export type State = {
    query: string;
    isContentReady: boolean;
}

export function SearchPage() {
    const [filter, setFilter] = createStore<Filter>({
        Gender: "",
        ArtistName: "",
        MovementId: "",
        NationalityId: "",
        ProfessionId: "",
        Page: 1,
        PageRows: 20,
        Order: "ByArtist",
        Direction: "Ascending"
    });
    const { formSubmit, validate, errors, setErrors } = useForm({
        errorClass: "error-input"
    });  
    const [page, setPage] = createSignal<number>(1);
    const [maxPage, setMaxPage] = createSignal<number>(1);
    const [search, setSearch] = createStore<Search>({ Count: 0, Page: 1, MaxPage: 1, Results: [] });
    const [state, setState] = createStore<State>({ isContentReady: false, query: "inactive" });
    const [resources, setResources] = createStore<Resources>({
        Genders: [],
        Movements: [],
        Nationalities: [],
        Professions: [],
        SortOptions: [
            { Text: "By name", Value: "ByName" },
            { Text: "By date added", Value: "ByDateAdded" }
        ],
        Directions: [
            { Text: "Ascending", Value: "Ascending" },
            { Text: "Descending", Value: "Descending" }
        ]
    });
    const loadSearch = async () => {
        setState("query", "active");
        const res = await fetchGet("/ArtistPortrait/SolidSearchJson", filter);
        const json = await res.json();
        if (!json.Success) {
            return;
        }
        setPage(json.Data.Page);
        setMaxPage(json.Data.MaxPage);
        const results = search.Results.concat(json.Data.Results);
        setSearch("Results", results);
        setState("query", "inactive");
    }
    const updatePage = async (p: number) => {
        setSearch("Results", []);
        setFilter("Page", p);
        await loadSearch();
    };
    const submitIfValid = async () => {
        setSearch("Results", []);
        await loadSearch();
    }
    const resetFields = () => {
        setFilter({
            ArtistName: "",
            MovementId: "",
            NationalityId: "",
            ProfessionId: "",
            Page: 1,
            PageRows: 20,
            Order: "ByName",
            Direction: "Ascending"
        });
        
    }
    const clearClicked = async (e: Event) => {
        e.preventDefault();
        resetFields();
        setSearch("Results", []);
        await loadSearch();
    }
    const loadNextPage = async () => {
        setFilter("Page", filter.Page + 1);
        await loadSearch();
    }
    const offset = 500;
    const handleScroll = async () => {
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight - offset) {
            if (state.query != "active") {
                if (page() < maxPage()) {
                    await loadNextPage();
                }
            }
        }
      };
    createEffect(async () => {
        window.addEventListener('scroll', handleScroll);
    });
    onCleanup(async () => {
        window.removeEventListener('scroll', handleScroll);
    });
    onMount(async () => {
        const routeData = await useRouteData() as any;
        setResources(routeData.resources);
        await loadSearch();
        setState("isContentReady", true);
    });
    const w = 300;
    return (
        <>
            <section class="page-heading" style="clear: both;">
                <h1>
                    Images of Artists
                </h1>
                <p>
                    <span>
                        <a href="/">Home</a> / <a href="/Museum/Home">Museum</a> / <a href="/ARChive/Home">ARChives</a>
                    </span>
                </p>
            </section>
            <div class="container">
                <section class="search m-b-xxl">
                    <ModelProvider model={filter} setModel={setFilter} validation={{ errors, validate }}>
                        <form use:formSubmit={submitIfValid}>
                            <div class="row">
                                <div class="col-md-4 col-xs-6">
                                    <ModelTextBox<Filter> field="ArtistName" label="Name" />
                                </div>
                                <div class="col-md-2 col-xs-6">
                                    <ModelSelect<Filter> field="Gender" list={resources.Genders} label="Gender" />
                                </div>
                                <div class="col-md-2 col-xs-6">
                                    <ModelSelect<Filter> field="MovementId" list={resources.Movements} label="Art movement/style" />
                                </div>
                                <div class="col-md-2 col-xs-6">
                                    <ModelSelect<Filter> field="ProfessionId" list={resources.Professions} label="Profession" />
                                </div>
                                <div class="col-md-2 col-xs-6">
                                    <ModelSelect<Filter> field="NationalityId" list={resources.Nationalities} label="Nationality" />
                                </div>
                            </div>
                            <div class="row" style="margin-top: 15px;">
                                <div class="col-md-2 col-xs-6">
                                    <ModelSelect<Filter> field="Order" list={resources.SortOptions} label="Sort by" />
                                </div>
                                <div class="col-md-2 col-xs-6">
                                    <ModelSelect<Filter> field="Direction" list={resources.Directions} label="Sort direction" />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-2" style="margin-top: 15px;">
                                    <button type="submit" class="btn btn-primary"><CMS id="button::search">Search</CMS></button>
                                    <button type="button" class="btn btn-default btn-white" onClick={clearClicked}><CMS id="button::clear">Clear</CMS></button>
                                </div>
                            </div>
                        </form>
                    </ModelProvider>
                    <div style="margin-top: 10px;">
                        <PageList page={page} maxPage={maxPage} onClick={updatePage} />
                    </div>
                </section>
            </div>
            <div>
                <div style="background-color: #333; position: relative; padding: 40px;">
                    <div class="finalist-grid">
                        <div class="grid-sizer"></div>
                        <div style="text-align: center;">
                            <For each={search.Results}>{(item) =>
                                <div class="grid-item" style={`display: inline-block; vertical-align: top; background-color: #000; margin-bottom: 20px; width: ${w}px;`}>
                                    <div style="position: relative; background-color: #000;">
                                        <A href={ `/index/${item.Id}` }>
                                            <Img
                                                src={item.PrimaryImage.URL} mode="crop" format="JPEG" width={w}
                                                style={`width: ${w}px; height: auto;`}
                                            />
                                        </A>
                                    </div>
                                    <div class="didactic" style="text-align: center; background-color: #fff; padding: 10px 15px;">
                                        <h1 style="font-family: 'Oswald'; font-size: 1.25rem;">
                                            <a href={`/artists/${item.Artist.Slug}/${item.Artist.Id}`}>{item.Artist.FirstName} {item.Artist.LastName}</a>
                                        </h1>
                                        <p style="margin-top: -10px;">
                                            {item.Artist.Nationality} {item.Artist.BirthYear||"?"}&mdash;{item.Artist.DeathYear||"?"}
                                        </p>
                                    </div>
                                </div>
                            }</For>
                            
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
